import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { Status, UserPresenceRecord, type PresenceRecord } from '@chancenwerk/shared/PresenceRecord.js';
import type { Mitarbeiter } from '@chancenwerk/shared/users/Mitarbeiter.js';
import type { ChromiumButton } from '@chancenwerk/client/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { switchMap } from 'rxjs';

@customElement('board-dialog-add-substitute-schuko-course')
export class BoardDialogAddSubstituteSchuKoCourse extends ChromiumDialog<string> {
  @property({ attribute: false }) kpfID = new RXController(this, '');
  @property({ attribute: false }) record: Maybe<PresenceRecord> = null;

  @state() private _schuko = new RXController(
    this,
    this.kpfID.observable.pipe(
      switchMap(async kpfID => {
        return getByID(
          KPF,
          kpfID,
        )({
          schuKos: {
            _id: 1,
            vorname: 1,
            nachname: 1,
            handynummer: 1,
            guestAccount: 1,
          },
        });
      }),
    ),
  );
  @state() private _selectedSchuko = new RXController<Maybe<Mitarbeiter>>(this, null);

  _submitButtonRef: Ref<ChromiumButton> = createRef();

  static override get element_name() {
    return 'board-dialog-add-substitute-schuko-course';
  }

  override heading = 'SchuKo-Vertretung hinzufügen';

  override content() {
    if (this._schuko.isLoading) {
      return html`<cr-flex space="md" crossaxis-center>
        <cr-loader></cr-loader>
        <cr-text>SchuKos werden gesucht...</cr-text>
      </cr-flex>`;
    }

    const schukos = (this._schuko.value?.schuKos ?? []).filter(s => {
      return !this.record?.schukoRecordsFiltered?.find(r => r.userID === s._id);
    });

    if (schukos.length === 0) {
      return html`<cr-flex space="md">
        <cr-text>Keine SchuKos verfügbar oder schon alle zugewiesen.</cr-text>
      </cr-flex>`;
    }

    return html`<cr-flex space="md">
      <cr-dropdown-search-selection
        placeholder="Schuko"
        .search=${(search: Maybe<string>) => {
          return schukos.filter(l => {
            if (!search) return true;
            return l.toString().toLowerCase().includes(search.toLowerCase());
          });
        }}
        .renderString=${(schuko: Maybe<Mitarbeiter>) => (schuko ?? '').toString()}
        @value-picked=${(e: ValueEvent<Mitarbeiter>) => {
          this._selectedSchuko.next(e.detail.value);
        }}
      >
      </cr-dropdown-search-selection>
      <cr-button @click=${this._saveLeKo} ${ref(this._submitButtonRef)}>Erstellen</cr-button>
    </cr-flex> `;
  }

  private async _saveLeKo() {
    if (!this.record) {
      void ChromiumSnackbar.show('Kein Kurs gewählt!');
      return;
    }

    if (!this._selectedSchuko.value) {
      void ChromiumSnackbar.show('Kein Schuko gewählt!');
      return;
    }

    if (this._submitButtonRef.value) {
      this._submitButtonRef.value.loading = true;
    }

    try {
      this.record.schukoRecords?.schukoRecords?.push(
        new UserPresenceRecord({
          userID: this._selectedSchuko.value._id,
          user: this._selectedSchuko.value,
          comment: 'SchuKo-Vertretung',
          date: this.record.date,
          status: Status.PRESENT,
        }),
      );

      await this.close(this._selectedSchuko.value._id);
    } catch (err: any) {
      void ChromiumSnackbar.show(err.message);
    } finally {
      if (this._submitButtonRef.value) {
        this._submitButtonRef.value.loading = false;
      }
    }
  }
}
